if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        
        // Initialize immediately to bind handlers before SliderComponent
        // Use requestAnimationFrame to ensure DOM is ready but still early
        requestAnimationFrame(() => {
          this.initialize();
        });
      }

      initialize() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        if (!this.elements.viewer) {
          console.warn('MediaGallery: Viewer not found');
          return;
        }
        
        if (!this.elements.thumbnails) {
          console.warn('MediaGallery: Thumbnails not found');
          return;
        }

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        
        // Completely replace the arrow buttons to remove all existing listeners
        const thumbnailSlider = this.elements.thumbnails;
        if (thumbnailSlider) {
          console.log('MediaGallery: Setting up button replacement');
          
          setTimeout(() => {
            const prevButton = thumbnailSlider.querySelector('button[name="previous"]');
            const nextButton = thumbnailSlider.querySelector('button[name="next"]');
            
            console.log('MediaGallery: Found buttons to replace', { prev: !!prevButton, next: !!nextButton });
            
            if (prevButton) {
              // Create a completely new button with same attributes
              const newPrev = document.createElement('button');
              newPrev.type = 'button';
              newPrev.className = prevButton.className;
              newPrev.name = 'previous';
              newPrev.setAttribute('aria-label', prevButton.getAttribute('aria-label') || 'Previous');
              newPrev.setAttribute('aria-controls', prevButton.getAttribute('aria-controls') || '');
              newPrev.setAttribute('data-step', prevButton.getAttribute('data-step') || '1');
              newPrev.innerHTML = prevButton.innerHTML;
              
              // Add our click handler
              newPrev.addEventListener('click', (e) => {
                console.log('MediaGallery: NEW Prev button clicked!');
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                this.handleArrowClick('previous');
              });
              
              prevButton.parentNode.replaceChild(newPrev, prevButton);
              console.log('MediaGallery: Prev button replaced');
            }
            
            if (nextButton) {
              // Create a completely new button with same attributes
              const newNext = document.createElement('button');
              newNext.type = 'button';
              newNext.className = nextButton.className;
              newNext.name = 'next';
              newNext.setAttribute('aria-label', nextButton.getAttribute('aria-label') || 'Next');
              newNext.setAttribute('aria-controls', nextButton.getAttribute('aria-controls') || '');
              newNext.setAttribute('data-step', nextButton.getAttribute('data-step') || '1');
              newNext.innerHTML = nextButton.innerHTML;
              
              // Add our click handler
              newNext.addEventListener('click', (e) => {
                console.log('MediaGallery: NEW Next button clicked!');
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                this.handleArrowClick('next');
              });
              
              nextButton.parentNode.replaceChild(newNext, nextButton);
              console.log('MediaGallery: Next button replaced');
            }
          }, 500);
        }
        
        // Use event delegation on the thumbnails container to catch all clicks
        // Listen on the slider-component itself to catch all events
        this.elements.thumbnails.addEventListener('click', (e) => {
          // Find the button or the data-target element
          let thumbnailItem = e.target.closest('[data-target]');
          if (!thumbnailItem) {
            const button = e.target.closest('button.thumbnail');
            if (button) {
              thumbnailItem = button.closest('[data-target]');
            }
          }
          
          // Also check if clicking on the image inside the button
          if (!thumbnailItem && e.target.tagName === 'IMG') {
            const button = e.target.closest('button.thumbnail');
            if (button) {
              thumbnailItem = button.closest('[data-target]');
            }
          }
          
          if (!thumbnailItem) return;
          
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          const mediaId = thumbnailItem.dataset.target;
          if (mediaId) {
            this.setActiveMedia(mediaId, false);
          }
        }, true); // Use capture phase to ensure it fires first
        
        // Also bind directly to buttons as backup
        const buttons = this.elements.thumbnails.querySelectorAll('button.thumbnail');
        buttons.forEach((button) => {
          button.addEventListener('click', (e) => {
            const thumbnailItem = button.closest('[data-target]');
            if (thumbnailItem) {
              e.preventDefault();
              e.stopPropagation();
              const mediaId = thumbnailItem.dataset.target;
              if (mediaId) {
                this.setActiveMedia(mediaId, false);
              }
            }
          }, true);
        });
        
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      handleArrowClick(direction) {
        const thumbnailSlider = this.elements.thumbnails;
        if (!thumbnailSlider) return;
        
        // Find the thumbnail list
        const slider = thumbnailSlider.querySelector('[id^="Slider-Thumbnails-"]') || 
                      thumbnailSlider.querySelector('.thumbnail-list');
        if (!slider) {
          console.warn('MediaGallery: Thumbnail list not found');
          return;
        }
        
        // Find the active main image
        const activeMainMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        if (!activeMainMedia) {
          console.warn('MediaGallery: Active main media not found');
          return;
        }
        
        const currentMediaId = activeMainMedia.dataset.mediaId;
        if (!currentMediaId) {
          console.warn('MediaGallery: Current media ID not found');
          return;
        }
        
        // Find all thumbnails
        const thumbnails = Array.from(slider.querySelectorAll('li[data-target], [data-target]'))
          .filter(thumb => thumb.dataset.target);
        
        if (thumbnails.length === 0) {
          console.warn('MediaGallery: No thumbnails found');
          return;
        }
        
        // Find current index
        const currentIndex = thumbnails.findIndex(thumb => thumb.dataset.target === currentMediaId);
        const startIndex = currentIndex === -1 ? 0 : currentIndex;
        
        // Determine next/previous index
        let targetIndex;
        if (direction === 'next') {
          targetIndex = (startIndex + 1) % thumbnails.length;
        } else {
          targetIndex = (startIndex - 1 + thumbnails.length) % thumbnails.length;
        }
        
        // Get the target thumbnail and update main image
        const targetThumbnail = thumbnails[targetIndex];
        if (targetThumbnail && targetThumbnail.dataset.target) {
          const mediaId = targetThumbnail.dataset.target;
          console.log('MediaGallery: Changing to media', mediaId);
          this.setActiveMedia(mediaId, false);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        // Find the slider list inside the viewer
        const sliderList = this.elements.viewer.querySelector('[id^="Slider-Gallery-"]') || 
                          this.elements.viewer.querySelector('.product__media-list');
        
        if (!sliderList) {
          return;
        }
        
        const activeMedia = sliderList.querySelector(`[data-media-id="${mediaId}"]`) ||
                           sliderList.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        
        // Remove active class from all media items
        sliderList.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        
        // Add active class to selected media
        activeMedia.classList.add('is-active');
        
        // Force a reflow to ensure the transition works
        void activeMedia.offsetWidth;

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        
        // For absolute positioned slides, we don't need to scroll - just ensure visibility
        // But keep the scroll logic for mobile/tablet layouts
        window.setTimeout(() => {
          const sliderList = activeMedia.parentElement;
          if (sliderList && sliderList.scrollWidth > sliderList.clientWidth) {
            // Only scroll if the slider is actually scrollable
            sliderList.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll page if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }, 50);
        
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
        
        // Prevent any scroll events on thumbnail slider from interfering
        // by temporarily disabling scroll event listeners
        const thumbnailSlider = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails-"]');
        if (thumbnailSlider) {
          // The thumbnail slider shouldn't scroll - all thumbnails are visible
          // But if it does scroll, we don't want it to trigger slideChanged events
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        // All thumbnails are visible, no scrolling needed
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
